import moment from 'moment';
import React, { lazy, memo, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { usePolyglot } from "../../../context/Polyglot";
import { sanitizeStringForRegex, useDebounce } from '../../../helpers';
import { useTypedDispatch, useTypedSelector } from "../../../redux/hooks";
import {
    chatApi,
} from "../../../redux/services/chatApi";
import {
    addOneToast,
    markAllChatsRead,
    selectActiveChat,
    selectCurrentUserId, selectFiltersByPage,
    selectIsNebulaChat,
    selectUnreadThreadMessages, setActiveChat, setChannelCreation, setPageFilter
} from '../../../redux/slices';
import { IChat, IChatMessage, IThread } from "../../../types";
import { AriaCheckbox } from "../../AriaComponents";
import Icon from "../../Icon";
import ChatListSkeleton from "../../Skeletons/ChatListSkeleton";
import { StyledButton } from "../../StyledComponents";
import StyledDropdown, { StyledDropdownOption } from "../../StyledComponents/StyledDropdown";
import { TextInput } from "../../forms";
import NewChat from "../../forms/NewChat";
import ModalFormContainer from "../../forms/modalFormContainer/ModalFormContainer";
import LoadingSpinner from "../../loading/LoadingSpinner";
import { Filters } from "../../menus/Filters";
import MenuHeader from "../../menus/MenuHeader";
import { useChatDataSet } from "./hooks";
import { nebulaChatApi } from '../../../redux/services/nebulaChat';
import ChatListN from './ChatListN';
import { selectActiveConversation, selectConversationThreadUnreadSum, setActiveConversation } from '../../../redux/slices/chatNSlice';


const ChatList = lazy(() => import('./ChatList'))

export type ChatDataType =
    IChatMessage
    | IChat
    | IThread
    | { divider: 'message' | 'empty', message: string }
    | { divider: 'loader' }

export type ChatListPageType = 'chats' | 'threads'

export const useChatsLoading = () => {
    const userUuid = useTypedSelector(selectCurrentUserId);

    const isNebulaChat = useTypedSelector(selectIsNebulaChat);

    const { isFetching: conversationsLoading } = nebulaChatApi.endpoints.getLastMessagesN.useQueryState({
        limit: 500,
        type: ['user', 'integration', 'channel']
    }, { skip: !isNebulaChat });
    const { isFetching: conversationThreadsLoading } = nebulaChatApi.endpoints.getLastMessagesN.useQueryState({
        limit: 500,
        type: ['thread']
    }, { skip: !isNebulaChat });

    const { isFetching: chatsLoading } = chatApi.endpoints.getLatestChats.useQueryState({
        voip_user_uuid: userUuid,
        type: 'user,channel',
        limit: 1000
    }, { skip: isNebulaChat });

    const { isLoading: threadsLoading } = chatApi.endpoints.getLatestChats.useQueryState({
        voip_user_uuid: userUuid,
        type: 'thread',
        limit: 1000
    }, { skip: isNebulaChat });

    return {
        chatsLoading: chatsLoading || conversationsLoading,
        threadsLoading: threadsLoading || conversationThreadsLoading
    }
}

const refresh_offset_seconds = 4;
let last_refresh: number | null = null;

const ChatRefreshBtn: React.FC = memo(() => {
    const { chatsLoading, threadsLoading } = useChatsLoading();

    const dispatch = useTypedDispatch();

    const handleRefresh = useCallback(async () => {
        const now = moment().unix()

        if (last_refresh) {
            const dif = now - last_refresh;

            // Only allow refresh every 10 min
            if (dif < refresh_offset_seconds) {
                dispatch(addOneToast({
                    type: 'warning',
                    title: 'Too Early to Re-Sync',
                    content: `Please wait ${refresh_offset_seconds - dif} seconds before trying to refresh chats again`
                }))
                return;
            }
        }

        last_refresh = now;

        await dispatch({ type: 'auth/reset_chat' });
        await dispatch(chatApi.util.invalidateTags(['chat_list', 'conversation_list', 'nebula_chat']));

        dispatch(setActiveChat(''));
        dispatch(setActiveConversation(undefined));
    }, [])

    const isLoading = chatsLoading || threadsLoading;

    return (
        <StyledButton
            buttonStyle='tertiary'
            size='large'
            className='menu-header__icon-extra-padding'
            iconOnly
            title='Sync Chats'
            loading={isLoading}
            disabled={isLoading}
            onClick={handleRefresh}
        >
            <Icon name='switchUser' />
        </StyledButton>
    );
})

const SideBarOLD: React.FC<{
    page: ChatListPageType,
    searchQueryDebounced: string,
    searchTerm: string
}> = ({
    page,
    searchQueryDebounced,
    searchTerm
}) => {

        const { chatsLoading, threadsLoading } = useChatsLoading();

        const { dataSet, chats, threads } = useChatDataSet({
            searchQueryDebounced,
            searchRegexDebounced: searchQueryDebounced ? new RegExp(sanitizeStringForRegex(searchQueryDebounced), 'i') : undefined,
            page
        });

        return (
            <Suspense fallback={<ChatListSkeleton />}>
                <ChatList
                    chatsLoading={page === 'chats' ? chatsLoading : threadsLoading}
                    dataSet={dataSet}
                    displayChats={page === 'chats' ? chats : threads}
                    page={page}
                    searchTerm={searchTerm}
                />
            </Suspense>
        );
    }

export const ChatSidebar: React.FC = () => {
    const isNebulaChat = useTypedSelector(selectIsNebulaChat);

    const groupCreationIsActive = useTypedSelector(state => state.chat.groupCreationActive);
    const filter = useTypedSelector(state => selectFiltersByPage(state, 'chatList'));

    const unreadThreadsCountOld = useTypedSelector(selectUnreadThreadMessages);
    const unreadConversationThread = useTypedSelector(selectConversationThreadUnreadSum);

    const unreadThreadsCount = isNebulaChat ? unreadConversationThread : unreadThreadsCountOld;

    const activeChat = useTypedSelector(selectActiveChat);
    const activeConversation = useTypedSelector(state => selectActiveConversation(state)?.uuid);

    const [page, setPage] = useState<ChatListPageType>('chats');
    const [searchTerm, setSearchTerm] = useState<string>('');

    const searchQueryDebounced = useDebounce(searchTerm, 500);

    const { t } = usePolyglot();

    const dispatch = useTypedDispatch();

    const filters = useMemo(() => ([
        [
            { title: 'misc', value: '' },
            { title: t('adjective.chats_unread'), value: 'Unread' },
            { title: t('adjective.chats_read'), value: 'Read' },
            { title: t('adjective.chats_pinned'), value: 'Pinned' },
            { title: t('adjective.chats_muted'), value: 'Muted' },
            { title: t('adjective.chats_sms'), value: 'SMS' },
        ],
    ]), [t]);

    const handleFilterChange = useCallback((selectedFilter: string) => {
        let newFilters: string[]

        switch (true) {
            case selectedFilter === 'Read' && filter.includes('Unread'):
                newFilters = filter
                    .filter(f => f !== 'Unread')
                    .concat(selectedFilter)
                break;
            case selectedFilter === 'Unread' && filter.includes('Read'):
                newFilters = filter
                    .filter(f => f !== 'Read')
                    .concat(selectedFilter)
                break;
            case selectedFilter === 'Groups' && filter.includes('Individual'):
                newFilters = filter
                    .filter(f => f !== 'Individual')
                    .concat(selectedFilter)
                break;
            case selectedFilter === 'Individual' && filter.includes('Groups'):
                newFilters = filter
                    .filter(f => f !== 'Groups')
                    .concat(selectedFilter)
                break;
            case filter.includes(selectedFilter):
                newFilters = filter
                    .filter(f => f !== selectedFilter)
                break;
            default:
                newFilters = filter.concat(selectedFilter)
        }

        dispatch(
            setPageFilter({
                page: 'chatList',
                newFilters
            })
        )
    }, [filter, dispatch]);

    useEffect(() => {
        if (searchTerm && (activeChat || activeConversation)) {
            setSearchTerm("")
        }
    }, [activeChat, activeConversation]);

    const dropDownOptions: StyledDropdownOption[] = useMemo(() => [{
        label: t("actions.mark_all_as_read"),
        value: 'mark_all_as_read',
    }], [t]);

    const handleDropdownOption = useCallback((val: string) => {
        switch (val) {
            case 'mark_all_as_read':
                dispatch(markAllChatsRead())
                break;
        }
    }, []);

    return (
        <div className='chat-sidebar'>
            <MenuHeader
                title={`${t("terms.chat", 1)} & ${t("abbreviations.sms")}`}
                titleOptions={
                    <div className='menu-header__icon-container'>
                        <StyledButton
                            buttonStyle='tertiary'
                            fontWeight='medium'
                            onClick={() => dispatch(setChannelCreation(true))}
                            title={t("phrases.new_chat")}
                        >
                            <Icon name='plus' width={24} height={24} />
                            <p>{t("phrases.new_chat")}</p>
                        </StyledButton>
                        <ChatRefreshBtn />
                        <StyledButton
                            buttonStyle='tertiary'
                            iconOnly
                            onClick={() => setPage(page === 'threads' ? 'chats' : 'threads')}
                            title={page === 'threads' ? t("actions.go_to_chats") : t("actions.go_to_threads")}
                            size='large'
                            className='menu-header__icon-extra-padding'
                        >
                            {page === 'threads' ? (
                                <Icon name='dualChatBubbleFill' ariaHidden width={20} height={20} />
                            ) : (
                                <>
                                    <Icon name='dualChatBubbleUnfill' ariaHidden width={20} height={20} />
                                    {unreadThreadsCount > 0 && (
                                        <i className='menu-header__icon-notification' />
                                    )}
                                </>
                            )}
                        </StyledButton>
                        <StyledDropdown
                            options={dropDownOptions}
                            iconOnly
                            onSelect={handleDropdownOption}
                            iconSize='large'
                        />
                    </div>
                }
                input={
                    <TextInput
                        name='Chat Search'
                        placeholder={t("actions.search_chat", 2)}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        isSearch
                        isSearching={searchTerm !== searchQueryDebounced}
                    />
                }
                filter={
                    <Filters
                        filter={filter}
                        filters={filters}
                        onChange={(f) => handleFilterChange(f)}
                        reset={() => {
                            dispatch(
                                setPageFilter({
                                    page: 'chatList',
                                    newFilters: []
                                })
                            )
                        }}
                        label='Chats Filter'
                    >
                        {page === 'chats' ? (
                            <>
                                <p>{t("terms.type", 1)}</p>
                                <AriaCheckbox value='Individual'>
                                    {t("adjective.chats_individual")}
                                </AriaCheckbox>
                                <AriaCheckbox value='Groups'>
                                    {t("terms.group", 2)}
                                </AriaCheckbox>
                            </>
                        ) : undefined}
                    </Filters>
                }
                selectedFilters={filter}
            />
            <ModalFormContainer
                showModal={groupCreationIsActive}
                setShowModal={(val: boolean) => dispatch(setChannelCreation(val))}
            >
                <Suspense fallback={<LoadingSpinner color='mamba' />}>
                    {groupCreationIsActive ? (
                        <NewChat
                            closeEditor={() => dispatch(setChannelCreation(false))}
                        />
                    ) : null}
                </Suspense>
            </ModalFormContainer>
            {isNebulaChat ? (
                <ChatListN searchQueryDebounced={searchQueryDebounced} is_threads={page === 'threads'} />
            ) : (
                <SideBarOLD
                    page={page}
                    searchQueryDebounced={searchQueryDebounced}
                    searchTerm={searchTerm}
                />
            )}
        </div>
    );
};
